/**
 * LevelNamesModal (S1.1) —「層級名稱 Level Names」
 *
 * The rename facility for the 5 bilingual level labels + L4/L5 enablement
 * (hide-don't-disable: levels 1–3 are core and render a lock tag instead of
 * a Switch). Validation mirrors the S0.4 level-config rule: no label may
 * duplicate a nav group name. One mandatory change comment covers the batch.
 */

import { useEffect, useMemo } from 'react';
import { App, Col, Input, Modal, Row, Switch, Tag, Typography, theme } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { extractGuardError, useTaxonomyMutations } from '@/hooks/useTaxonomy';
import { MIN_ENABLED_CATEGORY_DEPTH, NAV_GROUP_LABEL_DENYLIST } from '@/types/api/productCategory';
import type { CategoryLevelConfig, UpdateCategoryLevelRequest } from '@/types/api/productCategory';

const { Text } = Typography;

const noNavCollision = (label: string) =>
  !NAV_GROUP_LABEL_DENYLIST.some((nav) => nav.toLowerCase() === label.trim().toLowerCase());

const levelRowSchema = z.object({
  level: z.number(),
  labelEn: z
    .string()
    .trim()
    .min(1, 'taxonomy:validation.labelRequired')
    .refine(noNavCollision, 'taxonomy:validation.labelNavCollision'),
  labelZh: z
    .string()
    .trim()
    .min(1, 'taxonomy:validation.labelRequired')
    .refine(noNavCollision, 'taxonomy:validation.labelNavCollision'),
  enabled: z.boolean(),
});

const levelNamesSchema = z.object({
  levels: z.array(levelRowSchema),
  comment: z.string().trim().min(1, 'taxonomy:validation.commentRequired'),
});

type LevelNamesFormData = z.infer<typeof levelNamesSchema>;

export interface LevelNamesModalProps {
  open: boolean;
  levels: CategoryLevelConfig[];
  onClose: () => void;
  /** Called after a successful save (page refreshes levels + tree). */
  onSaved: () => void;
}

const LevelNamesModal = ({ open, levels, onClose, onSaved }: LevelNamesModalProps) => {
  const { t } = useTranslation(['taxonomy', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();
  const { updateLevels } = useTaxonomyMutations();

  const formValues = useMemo<LevelNamesFormData>(
    () => ({
      levels: levels.map((l) => ({
        level: l.level,
        labelEn: l.labelEn,
        labelZh: l.labelZh,
        enabled: l.enabled,
      })),
      comment: '',
    }),
    [levels]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LevelNamesFormData>({
    resolver: zodResolver(levelNamesSchema),
    defaultValues: formValues,
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(formValues);
  }, [open, formValues, reset]);

  const submit = async (data: LevelNamesFormData) => {
    // PATCH only the levels that actually changed
    const changes: Array<{ level: number; patch: UpdateCategoryLevelRequest }> = [];
    for (const row of data.levels) {
      const original = levels.find((l) => l.level === row.level);
      if (!original) continue;
      const patch: UpdateCategoryLevelRequest = { comment: data.comment.trim() };
      let dirty = false;
      if (row.labelEn.trim() !== original.labelEn) {
        patch.labelEn = row.labelEn.trim();
        dirty = true;
      }
      if (row.labelZh.trim() !== original.labelZh) {
        patch.labelZh = row.labelZh.trim();
        dirty = true;
      }
      if (row.enabled !== original.enabled) {
        patch.enabled = row.enabled;
        dirty = true;
      }
      if (dirty) changes.push({ level: row.level, patch });
    }

    if (changes.length === 0) {
      onClose();
      return;
    }

    try {
      await updateLevels(changes);
      message.success(t('taxonomy:levels.success'));
      onClose();
      onSaved();
    } catch (error) {
      const guard = extractGuardError(error);
      message.error(guard ? t(`taxonomy:errors.${guard}`) : t('common:messages.saveFailed'));
    }
  };

  const rowError = (index: number, field: 'labelEn' | 'labelZh') => {
    const fieldError = errors.levels?.[index]?.[field];
    return fieldError ? (
      <Text style={formStyles.errorStyle}>{t(fieldError.message ?? '')}</Text>
    ) : null;
  };

  return (
    <Modal
      open={open}
      title={t('taxonomy:levels.title')}
      okText={t('common:actions.save')}
      cancelText={t('common:actions.cancel')}
      onCancel={onClose}
      onOk={handleSubmit(submit)}
      confirmLoading={isSubmitting}
      width={800}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      destroyOnHidden
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
        {t('taxonomy:levels.hint')}
      </Text>

      {formValues.levels.map((row, index) => {
        const isCore = row.level <= MIN_ENABLED_CATEGORY_DEPTH;
        return (
          <div key={row.level} style={formStyles.fieldContainerStyle}>
            <Row gutter={[16, 8]} align="middle">
              <Col span={4}>
                <Text strong>{t('taxonomy:levels.level', { level: row.level })}</Text>
              </Col>
              <Col span={8}>
                <Text style={formStyles.labelStyle}>{t('taxonomy:levels.labelEn')}</Text>
                <Controller
                  name={`levels.${index}.labelEn`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      style={formStyles.inputStyle}
                      status={errors.levels?.[index]?.labelEn ? 'error' : undefined}
                    />
                  )}
                />
                {rowError(index, 'labelEn')}
              </Col>
              <Col span={8}>
                <Text style={formStyles.labelStyle}>{t('taxonomy:levels.labelZh')}</Text>
                <Controller
                  name={`levels.${index}.labelZh`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      style={formStyles.inputStyle}
                      status={errors.levels?.[index]?.labelZh ? 'error' : undefined}
                    />
                  )}
                />
                {rowError(index, 'labelZh')}
              </Col>
              <Col span={4} style={{ textAlign: 'right' }}>
                {isCore ? (
                  <Tag bordered={false}>{t('taxonomy:levels.core')}</Tag>
                ) : (
                  <>
                    <Text style={formStyles.labelStyle}>{t('taxonomy:levels.enabled')}</Text>
                    <Controller
                      name={`levels.${index}.enabled`}
                      control={control}
                      render={({ field }) => (
                        <Switch checked={field.value} onChange={field.onChange} />
                      )}
                    />
                  </>
                )}
              </Col>
            </Row>
          </div>
        );
      })}

      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
        {t('taxonomy:levels.enableHint')}
      </Text>

      <Text style={formStyles.labelStyle}>
        <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
        {t('taxonomy:fields.comment')}
      </Text>
      <Controller
        name="comment"
        control={control}
        render={({ field }) => (
          <Input.TextArea
            {...field}
            rows={2}
            placeholder={t('taxonomy:detail.commentPlaceholder')}
            style={{ ...formStyles.inputStyle, height: 'auto' }}
            status={errors.comment ? 'error' : undefined}
          />
        )}
      />
      {errors.comment && (
        <Text style={formStyles.errorStyle}>{t(errors.comment.message ?? '')}</Text>
      )}
    </Modal>
  );
};

export default LevelNamesModal;
